/**
 * OrgTreeSelect
 * Återanvändbar organisationsväljare med hierarkisk trädvy (Hastighetsefterlevnad-stil)
 * Enkel dropdown med chevron för expand/kollaps och sökning
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { useOrganizationTree } from '@/hooks/useOrganizationTree';

const normalize = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export interface OrgTreeNode {
  id: string;
  name: string;
  children?: OrgTreeNode[];
}

interface OrgTreeSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Visa "Alla organisationer" / tomt alternativ */
  showEmptyOption?: boolean;
  /** Etikett för tomt alternativ */
  emptyLabel?: string;
  /** Chip-variant för filterrad (t.ex. Fordonsflottan) */
  variant?: 'default' | 'chip';
  /** Etikett i chip-variant, t.ex. "ORGANISATION" */
  chipLabel?: string;
  /** Markera chip som aktivt (grön kant) */
  isActive?: boolean;
}

/** Hitta org i trädstrukturen */
function findOrgInTree(orgs: OrgTreeNode[], id: string): OrgTreeNode | null {
  const normalized = String(id);
  for (const org of orgs) {
    if (String(org.id) === normalized) return org;
    if (org.children?.length) {
      const found = findOrgInTree(org.children, normalized);
      if (found) return found;
    }
  }
  return null;
}

/** Flatten träd till lista med depth för sökning */
function flattenWithDepth(orgs: OrgTreeNode[], depth = 0): { id: string; name: string; depth: number }[] {
  const result: { id: string; name: string; depth: number }[] = [];
  for (const org of orgs) {
    result.push({ id: org.id, name: org.name, depth });
    if (org.children?.length) {
      result.push(...flattenWithDepth(org.children, depth + 1));
    }
  }
  return result;
}

/** Hämta id:n för alla förfäder till en nod (så att trädet kan expanderas till vald nod) */
function getAncestorIds(orgs: OrgTreeNode[], targetId: string, path: string[] = []): string[] | null {
  for (const org of orgs) {
    if (org.id === targetId) return path;
    if (org.children?.length) {
      const found = getAncestorIds(org.children, targetId, [...path, org.id]);
      if (found) return found;
    }
  }
  return null;
}

const OrgTreeSelect: React.FC<OrgTreeSelectProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  className = '',
  showEmptyOption = false,
  emptyLabel,
  variant = 'default',
  chipLabel,
  isActive = false,
}) => {
  const { t } = useTranslation();
  const { tree: organizations, loading } = useOrganizationTree();
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOrgName =
    !value && showEmptyOption
      ? (emptyLabel ?? t('fleet.filterAllOrganizations') ?? 'Alla organisationer')
      : findOrgInTree(organizations, value)?.name ?? placeholder ?? t('common.selectOrganization') ?? 'Välj organisation';

  const flatOrgs = useMemo(() => flattenWithDepth(organizations), [organizations]);
  const searchNorm = normalize(search.trim());
  const filteredFlatOrgs = useMemo(
    () =>
      searchNorm
        ? flatOrgs.filter((o) => normalize(o.name).includes(searchNorm))
        : [],
    [flatOrgs, searchNorm]
  );
  const isSearchMode = searchNorm.length > 0;

  // Auto-välj första org när träd laddats och ingen tom option
  useEffect(() => {
    if (!loading && organizations.length > 0 && !showEmptyOption && !value) {
      onChange(organizations[0].id);
    }
  }, [loading, organizations, showEmptyOption, value, onChange]);

  // Stäng dropdown vid klick utanför
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // När dropdown öppnas: rensa sökning, fokusera sökfält, expandera föräldrakedjan till vald nod
  useEffect(() => {
    if (showDropdown) {
      setSearch('');
      setTimeout(() => searchInputRef.current?.focus(), 0);
      if (value && organizations.length > 0) {
        const ancestorIds = getAncestorIds(organizations, value);
        if (ancestorIds && ancestorIds.length > 0) {
          setExpandedOrgs((prev) => new Set([...prev, ...ancestorIds]));
        }
      }
    }
  }, [showDropdown, value, organizations]);

  const toggleExpand = useCallback((orgId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedOrgs((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  }, []);

  const selectOrg = useCallback(
    (orgId: string) => {
      onChange(orgId ? String(orgId) : '');
      setShowDropdown(false);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange('');
    setShowDropdown(false);
  }, [onChange]);

  const renderOrgNode = (
    org: OrgTreeNode,
    depth: number,
    expanded: Set<string>,
    selected: string,
    onToggle: (id: string, e: React.MouseEvent) => void,
    onSelect: (id: string) => void
  ): React.ReactNode => {
    const hasChildren = org.children && org.children.length > 0;
    const isExpanded = expanded.has(org.id);

    return (
      <div key={org.id}>
        <div
          className={`flex items-center py-2 px-3 hover:bg-bgBase cursor-pointer ${
            selected === org.id ? 'bg-primary/10' : ''
          }`}
          style={{ paddingLeft: `${depth * 16 + 12}px` }}
          onClick={() => onSelect(org.id)}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => onToggle(org.id, e)}
              className="mr-2 p-0.5 hover:bg-bgContainer rounded shrink-0"
            >
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <span className="w-6 shrink-0" />
          )}
          <span className="text-sm text-textDefault">{org.name}</span>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {org.children!.map((child) =>
              renderOrgNode(child, depth + 1, expanded, selected, onToggle, onSelect)
            )}
          </div>
        )}
      </div>
    );
  };

  const triggerClassName =
    variant === 'chip'
      ? `w-full px-3 py-1.5 text-sm rounded-md border text-left flex items-center justify-between gap-2 disabled:opacity-50 transition-colors ${
          isActive
            ? 'border-teal-500 bg-teal-50/60 dark:bg-teal-900/20'
            : 'border-border bg-bgContainer hover:border-border/80'
        }`
      : 'w-full px-3 py-2 border border-border rounded-lg bg-bgElevated text-textDefault text-left flex items-center justify-between disabled:opacity-50';

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && !loading && setShowDropdown(!showDropdown)}
        disabled={disabled || loading}
        className={triggerClassName}
      >
        {variant === 'chip' && chipLabel ? (
          <span className="flex items-center gap-1.5 min-w-0 truncate text-sm">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted shrink-0">
              {chipLabel}
            </span>
            <span className="text-text-muted shrink-0">|</span>
            <span
              className={`truncate ${isActive ? 'font-medium text-teal-700 dark:text-teal-300' : 'text-textDefault'}`}
            >
              {loading ? '...' : selectedOrgName}
            </span>
          </span>
        ) : (
          <span className="truncate">{loading ? '...' : selectedOrgName}</span>
        )}
        <svg
          className={`w-4 h-4 text-textSecondary shrink-0 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showDropdown && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} aria-hidden="true" />
          <div className="absolute z-50 w-full mt-1 bg-bgElevated border border-border rounded-lg shadow-lg min-w-[200px]">
            {/* Sökfält */}
            <div className="p-2 border-b border-border sticky top-0 bg-bgElevated">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-textSecondary" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('common.search') ?? 'Sök...'}
                  className="w-full pl-8 pr-8 py-1.5 text-sm border border-border rounded bg-bgBase text-textDefault placeholder-textSecondary focus:outline-none focus:ring-2 focus:ring-primary"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                  >
                    <XMarkIcon className="h-4 w-4 text-textSecondary hover:text-textDefault" />
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto py-1">
              {showEmptyOption && (
                <>
                  <button
                    type="button"
                    onClick={handleClear}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-bgBase transition-colors ${
                      !value ? 'bg-primary/10 text-primary font-medium' : 'text-textDefault'
                    }`}
                  >
                    {emptyLabel ?? t('fleet.filterAllOrganizations') ?? 'Alla organisationer'}
                  </button>
                  <div className="border-t border-border my-1" />
                </>
              )}
              {isSearchMode ? (
                <>
                  {filteredFlatOrgs.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-textSecondary text-center">
                      {t('common.noResults') ?? 'Inga resultat'}
                    </div>
                  ) : (
                    filteredFlatOrgs.map((o) => (
                      <div
                        key={o.id}
                        className={`flex items-center py-2 px-3 hover:bg-bgBase cursor-pointer ${
                          value === o.id ? 'bg-primary/10' : ''
                        }`}
                        style={{ paddingLeft: `${o.depth * 16 + 12}px` }}
                        onClick={() => selectOrg(o.id)}
                      >
                        <span className="text-sm text-textDefault">{o.name}</span>
                      </div>
                    ))
                  )}
                </>
              ) : (
                organizations.map((org) =>
                  renderOrgNode(org, 0, expandedOrgs, value, toggleExpand, selectOrg)
                )
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrgTreeSelect;
